<template>
    <v-container fluid>
        <!-- Breadcrumbs -->
        <v-breadcrumbs
            :items="[
                { text: 'Home', disable: false, href: '/#/' },
                { text: 'Suppliers', disabled: true }
            ]"
            divider="/"
        ></v-breadcrumbs>

        <!-- Supplier Dialog -->
        <SupplierEditDialog
            :object.sync="editedItem"
            :dialog.sync="supplierDialog"
            v-on:item-created="addSupplierConfirmed()"
        ></SupplierEditDialog>

        <!-- Delete Supplier Dialog -->
        <DeleteDialog
            :dialog.sync="deleteDialog"
            :item="deletedSupplier"
            v-on:confirmed="deletingConfirmed"
        >
            <template v-slot:title
                >Delete -
                {{ deletedSupplier ? deletedSupplier.name : "" }}</template
            >
            <template v-slot:content
                >Are you sure you want to delete this supplier?</template
            >
        </DeleteDialog>

        <v-row class="ma-0">
            <v-col cols="12" class="pt-0">
                <v-hover>
                    <template v-slot="{ hover }">
                        <v-card :elevation="hover ? 6 : 1">
                            <v-card-title class="pb-0">
                                <v-icon color="primary" class="ml-2 mr-2"
                                    >spa</v-icon
                                >Suppliers
                                <v-spacer></v-spacer>
                                <v-col cols="12" sm="4">
                                    <v-text-field
                                        dense
                                        v-model="search"
                                        append-outer-icon="refresh"
                                        @click:append-outer="
                                            refreshCurrentPage()
                                        "
                                        label="Search"
                                        clearable
                                        hide-details
                                        @keydown.enter="refreshCurrentPage()"
                                        @click:clear="onClearSearch()"
                                    ></v-text-field>
                                </v-col>
                                <v-spacer></v-spacer>
                                <v-btn
                                    v-if="hasPermission('add_supplier')"
                                    color="primary"
                                    dark
                                    outlined
                                    rounded
                                    small
                                    class="mx-2"
                                    @click="addSupplier()"
                                    >New Supplier</v-btn
                                >
                            </v-card-title>

                            <v-container fluid>
                                <v-data-table
                                    :headers="headers"
                                    :loading="$apollo.queries.suppliers.loading"
                                    :items="suppliers.edges"
                                    :options.sync="dataTableOptions"
                                    :server-items-length="suppliers.totalCount"
                                    loading-text="Loading... Please wait"
                                    :items-per-page="defaultItemsPerPage"
                                    :footer-props="{
                                        'items-per-page-options': [
                                            10,
                                            25,
                                            50,
                                            75,
                                            100
                                        ],
                                        'disable-pagination':
                                            $apollo.queries.suppliers.loading
                                    }"
                                >
                                    <template
                                        v-slot:item.node.country="{ item }"
                                    >
                                        <span>{{
                                            countries
                                                .filter(
                                                    element =>
                                                        element.code ===
                                                        item.node.country
                                                )
                                                .map(element => element.name)[0]
                                        }}</span>
                                        <!-- <v-icon small @click="deletingPlant(item)">delete</v-icon> -->
                                    </template>
                                    <template v-slot:item.action="{ item }">
                                        <v-btn
                                            v-if="
                                                hasPermission('change_supplier')
                                            "
                                            outlined
                                            dark
                                            fab
                                            x-small
                                            color="primary"
                                            class="mr-2"
                                            @click="editSupplier(item.node)"
                                        >
                                            <v-icon>edit</v-icon>
                                        </v-btn>
                                        <v-btn
                                            v-if="
                                                hasPermission('delete_supplier')
                                            "
                                            outlined
                                            dark
                                            fab
                                            x-small
                                            color="error"
                                            @click="deletingSupplier(item.node)"
                                        >
                                            <v-icon>delete</v-icon>
                                        </v-btn>
                                        <!-- <v-icon small @click="deletingPlant(item)">delete</v-icon> -->
                                    </template>
                                </v-data-table>
                            </v-container>
                        </v-card>
                    </template>
                </v-hover>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import gql from "graphql-tag";
import helper from "@/utils/helper.js";
import * as countries from "@/utils/countries.js";
import SupplierEditDialog from "@/components/tenants/SupplierEditDialog.vue";
import DeleteDialog from "@/components/base/DeleteDialog.vue";

export default {
    name: "supplier-list-page",
    components: {
        SupplierEditDialog,
        DeleteDialog
    },

    apollo: {
        suppliers: {
            query: gql`
                query suppliers(
                    $first: Int
                    $last: Int
                    $before: String
                    $after: String
                    $search: String
                    $orderBy: String
                ) {
                    suppliers(
                        first: $first
                        last: $last
                        before: $before
                        after: $after
                        search: $search
                        orderBy: $orderBy
                    ) {
                        edgeCount
                        totalCount
                        pageInfo {
                            startCursor
                            endCursor
                            hasPreviousPage
                            hasNextPage
                        }
                        edges {
                            node {
                                id
                                name
                                description
                                address1
                                address2
                                zipCode
                                city
                                country
                                externalSupplierId
                            }
                        }
                    }
                }
            `,
            variables() {
                return {
                    first: 10
                };
            },
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => data.suppliers,
            skip: true,
            pollInterval: 60000 // ms
        }
    },

    data: function() {
        return {
            supplierDialog: false,
            deleteDialog: false,
            search: "",

            defaultItemsPerPage: 25,
            dataTableOptions: {},
            page: 1,

            suppliers: {},

            editedItem: {},
            defaultItem: {
                id: "",
                name: "",
                zipCode: null,
                city: null,
                country: null
            },
            deletedSupplier: {},

            countries: countries,

            headers: [
                {
                    text: "External ID",
                    value: "node.externalSupplierId",
                    sortable: true
                },
                {
                    text: "Supplier",
                    value: "node.name",
                    sortable: true
                },
                {
                    text: "Zip Code",
                    value: "node.zipCode",
                    sortable: true
                },
                {
                    text: "City",
                    value: "node.city",
                    sortable: true
                },
                {
                    text: "Country",
                    value: "node.country",
                    sortable: true
                },
                {
                    text: "Action",
                    align: "center",
                    value: "action",
                    sortable: false
                }
            ],
            editedIndex: -1
        };
    },
    computed: {},
    watch: {
        dataTableOptions: {
            handler() {
                this.refreshCurrentPage();
            },
            deep: true
        }
    },
    created() {
        this.hasPermission = helper.hasPermission;
    },
    methods: {
        onClearSearch() {
            this.search = "";

            this.refreshCurrentPage();
        },

        refreshCurrentPage() {
            const {
                sortBy,
                sortDesc,
                page,
                itemsPerPage
            } = this.dataTableOptions;

            var _first = itemsPerPage;
            var _last = null;
            var _before = null;
            var _after = null;
            var _orderBy = null;

            if (page < this.page) {
                _first = null;
                _last = itemsPerPage;
                _before = this.suppliers.pageInfo.startCursor;
                _after = null;
            }

            if (page > this.page) {
                _first = itemsPerPage;
                _last = null;
                _before = null;
                _after = this.suppliers.pageInfo.endCursor;
            }

            switch (sortBy[0]) {
                case "node.externalSupplierId":
                    _orderBy = "externalSupplierId";
                    break;
                case "node.name":
                    _orderBy = "name";
                    break;
                case "node.zipCode":
                    _orderBy = "zipCode";
                    break;
                case "node.city":
                    _orderBy = "city";
                    break;
                case "node.country":
                    _orderBy = "country";
                    break;
                default:
                    _orderBy = "name";
                    break;
            }

            if (sortDesc[0] === true) {
                _orderBy = "-" + _orderBy;
            }

            this.$apollo.queries.suppliers.setVariables({
                first: _first,
                last: _last,
                before: _before,
                after: _after,
                orderBy: _orderBy,
                search: this.search
            });

            // set skip false to execute the query
            this.$apollo.queries.suppliers.skip = false;
            this.$apollo.queries.suppliers.refresh();

            // update this.page
            this.page = page;
        },

        addSupplier() {
            this.editedItem = null;
            this.supplierDialog = true;
        },

        addSupplierConfirmed() {
            this.$apollo.queries.suppliers.refresh();
        },

        editSupplier(supplier) {
            this.editedItem = JSON.parse(JSON.stringify(supplier));

            this.supplierDialog = true;
        },

        deletingSupplier(item) {
            this.deletedSupplier = item;
            this.deleteDialog = true;
        },

        deletingConfirmed(item) {
            if (item) {
                const payload = {
                    id: item.id
                };

                this.$store
                    .dispatch("suppliers/deleteSupplier", payload)
                    .then(response => {
                        // refresh plants
                        this.$apollo.queries.suppliers.refresh();

                        // close delete dialog
                        this.deleteDialog = false;

                        // show snackbar
                        const payload = {
                            color: "success",
                            message: `${response.name} is deleted!`
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);
                    })
                    .catch(error => {
                        this.error = error.graphQLErrors[0].message;
                    });
            }
        }
    }
};
</script>